import React, { useEffect, useState } from "react";
import styles from "./AdminBulkSend.module.scss";
import {
  AdminDataService,
  LicenseHolder,
  CommunicationTemplate,
} from "./mock-data/admin-data.service";
import Button from "../../components/Button/Button";
import { useHistory } from "react-router-dom";
import { UIContext } from "../../shared/shared-with-mobile/providers/ui.provider";
import { APIService } from "../../shared/shared-with-mobile/api-client/api.service";
import { BulkRecipient } from "../../shared/shared-with-mobile/api-client/services/communications.service";

const MAX_BULK_EMAIL = 500;
const MAX_BULK_SMS = 100;

const AdminBulkSend: React.FC = () => {
  const history = useHistory();
  const { dispatchToast } = React.useContext(UIContext);
  const [loading, setLoading] = useState(true);
  const [recipients, setRecipients] = useState<LicenseHolder[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [channel, setChannel] = useState<"email" | "sms">("email");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templates, setTemplates] = useState<CommunicationTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [scheduleType, setScheduleType] = useState<"now" | "later">("now");
  const [
    previewRecipient,
    setPreviewRecipient,
  ] = useState<LicenseHolder | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    async function load() {
      await AdminDataService.initialize();
      // Pre-select top 25 active accounts as default recipients
      const holders = AdminDataService.getLicenseHolders();
      const active = holders
        .filter((h) => h.healthScore >= 40)
        .sort((a, b) => b.questions - a.questions)
        .slice(0, 25);
      setRecipients(active);
      setSelectedEmails(new Set(active.map((h) => h.email)));
      setPreviewRecipient(active[0] || null);
      setTemplates(AdminDataService.getEmailTemplates());
      setLoading(false);
    }
    load();
  }, []);

  const toggleRecipient = (email: string) => {
    const next = new Set(selectedEmails);
    if (next.has(email)) next.delete(email);
    else next.add(email);
    setSelectedEmails(next);
  };

  const loadTemplate = (id: string) => {
    setSelectedTemplate(id);
    const tmpl = templates.find((t) => t.id === id);
    if (tmpl) {
      setSubject(tmpl.subject);
      setBody(tmpl.body);
    }
  };

  const resolveTokens = (text: string, holder: LicenseHolder): string => {
    return text
      .replace(/\{\{coach_name\}\}/g, holder.owner)
      .replace(/\{\{team_name\}\}/g, holder.teamName)
      .replace(/\{\{tier\}\}/g, holder.tier)
      .replace(/\{\{questions_count\}\}/g, holder.questions.toLocaleString())
      .replace(/\{\{player_count\}\}/g, holder.users.toString())
      .replace(/\{\{[^}]+\}\}/g, "[...]");
  };

  const insertToken = (token: string) => {
    setBody((prev) => prev + `{{${token}}}`);
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  const selectedCount = selectedEmails.size;

  return (
    <div className={styles.page}>
      <div className={styles.backNav}>
        <a
          href="#"
          className={styles.backLink}
          onClick={(e) => {
            e.preventDefault();
            history.push("/admin/communications");
          }}
        >
          &larr; Back to Communications
        </a>
      </div>

      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Bulk Send</h1>
          <p className={styles.subtitle}>
            Send email or SMS to multiple recipients
          </p>
        </div>
      </div>

      <div className={styles.twoColumn}>
        {/* Left: Recipients + Compose */}
        <div className={styles.leftColumn}>
          {/* Recipients */}
          <div className={styles.card}>
            <div className={styles.cardTitle}>
              Recipients ({selectedCount} selected)
            </div>
            <div className={styles.recipientList}>
              {recipients.map((r) => (
                <label key={r.email} className={styles.recipientRow}>
                  <input
                    type="checkbox"
                    checked={selectedEmails.has(r.email)}
                    onChange={() => toggleRecipient(r.email)}
                  />
                  <span className={styles.recipientName}>{r.owner}</span>
                  <span className={styles.recipientEmail}>{r.email}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Compose */}
          <div className={styles.card}>
            <div className={styles.composeHeader}>
              <div className={styles.cardTitle}>Compose</div>
              <div className={styles.channelToggle}>
                <button
                  className={channel === "email" ? styles.channelActive : ""}
                  onClick={() => setChannel("email")}
                >
                  Email
                </button>
                <button
                  className={channel === "sms" ? styles.channelActive : ""}
                  onClick={() => setChannel("sms")}
                >
                  SMS
                </button>
              </div>
            </div>

            <div className={styles.formField}>
              <label>From</label>
              <div className={styles.readonlyField}>
                Team Nation Sports &lt;noreply@teamnationsports.com&gt;
              </div>
            </div>

            {channel === "email" && (
              <div className={styles.formField}>
                <label>Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter subject..."
                  className={styles.input}
                />
              </div>
            )}

            <div className={styles.formField}>
              <label>Template</label>
              <select
                value={selectedTemplate}
                onChange={(e) => loadTemplate(e.target.value)}
                className={styles.input}
              >
                <option value="">Select a template...</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formField}>
              <div className={styles.tokenBar}>
                <span className={styles.tokenLabel}>Insert:</span>
                {["coach_name", "team_name", "tier", "questions_count"].map(
                  (t) => (
                    <button
                      key={t}
                      className={styles.tokenBtn}
                      onClick={() => insertToken(t)}
                    >
                      {t}
                    </button>
                  )
                )}
              </div>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write your message..."
                rows={8}
                className={styles.input}
              />
            </div>

            <div className={styles.scheduleRow}>
              <label>
                <input
                  type="radio"
                  name="schedule"
                  checked={scheduleType === "now"}
                  onChange={() => setScheduleType("now")}
                />{" "}
                Send Now
              </label>
              <label>
                <input
                  type="radio"
                  name="schedule"
                  checked={scheduleType === "later"}
                  onChange={() => setScheduleType("later")}
                />{" "}
                Schedule for Later
              </label>
            </div>
          </div>
        </div>

        {/* Right: Preview + Send */}
        <div className={styles.rightColumn}>
          <div className={styles.card}>
            <div className={styles.previewHeader}>
              <div className={styles.cardTitle}>Live Preview</div>
              <select
                className={styles.previewSelect}
                onChange={(e) => {
                  const r = recipients.find((r) => r.email === e.target.value);
                  if (r) setPreviewRecipient(r);
                }}
                value={previewRecipient?.email || ""}
              >
                {recipients.slice(0, 10).map((r) => (
                  <option key={r.email} value={r.email}>
                    {r.owner}
                  </option>
                ))}
              </select>
            </div>

            {previewRecipient && (
              <>
                <div className={styles.previewMeta}>
                  <div>
                    <strong>From:</strong> Team Nation Sports
                  </div>
                  <div>
                    <strong>To:</strong> {previewRecipient.email}
                  </div>
                  {channel === "email" && (
                    <div>
                      <strong>Subject:</strong>{" "}
                      {resolveTokens(subject, previewRecipient)}
                    </div>
                  )}
                </div>
                <div className={styles.previewBody}>
                  {resolveTokens(body, previewRecipient)}
                </div>
              </>
            )}
          </div>

          <div className={styles.sendActions}>
            <Button
              theme="secondary"
              size="small"
              onClick={() =>
                dispatchToast({
                  type: "success",
                  message: "Draft saved",
                })
              }
            >
              Save as Draft
            </Button>
            <Button
              theme="primary"
              size="medium"
              onClick={() => setShowConfirm(true)}
            >
              Send to {selectedCount} Recipients
            </Button>
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      {showConfirm && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowConfirm(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div
              style={{ textAlign: "center", fontSize: 36, marginBottom: 12 }}
            >
              &#9993;
            </div>
            <h3 style={{ textAlign: "center", marginBottom: 8 }}>
              Confirm Send
            </h3>
            <p
              style={{
                textAlign: "center",
                color: "#ababb7",
                marginBottom: 20,
              }}
            >
              You&apos;re about to send this{" "}
              {channel === "email" ? "email" : "SMS"} to{" "}
              <strong style={{ color: "#fff" }}>
                {selectedCount} recipients
              </strong>
              .
            </p>
            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "center",
              }}
            >
              <Button
                theme="secondary"
                size="small"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                theme="primary"
                size="small"
                onClick={async () => {
                  if (sending) return;
                  setSending(true);
                  try {
                    const cap =
                      channel === "email" ? MAX_BULK_EMAIL : MAX_BULK_SMS;
                    if (selectedEmails.size > cap) {
                      dispatchToast({
                        type: "error",
                        message: `Max ${cap} ${channel} recipients per send. Reduce selection or split into multiple sends.`,
                      });
                      setSending(false);
                      return;
                    }
                    const selectedHolders = recipients.filter((h) =>
                      selectedEmails.has(h.email)
                    );
                    if (channel === "sms") {
                      // Mock LicenseHolder has no phone field. Bulk SMS will need
                      // a recipient list pulled from a phone-aware data source
                      // (engagement endpoint extension) before this is useful.
                      dispatchToast({
                        type: "error",
                        message:
                          "Bulk SMS recipient list needs phone numbers — not yet wired in this UI. Use the Customer Profile → Compose SMS for individual sends.",
                      });
                      setSending(false);
                      return;
                    }
                    const apiRecipients: BulkRecipient[] = selectedHolders.map(
                      (h) => ({
                        recipient: h.email,
                        variables: {
                          coach_name: h.owner,
                          team_name: h.teamName,
                          tier: h.tier,
                          questions_count: h.questions.toLocaleString(),
                          player_count: String(h.users),
                        },
                      })
                    );
                    const result = await APIService.COMMUNICATIONS.SEND_BULK({
                      channel,
                      template: body,
                      subject: channel === "email" ? subject : undefined,
                      recipients: apiRecipients.filter((r) => r.recipient),
                      templateName: selectedTemplate || undefined,
                    });
                    setShowConfirm(false);
                    const blockedNote =
                      result.blockedDev > 0
                        ? ` (${result.blockedDev} blocked in dev)`
                        : "";
                    const failedNote =
                      result.failed > 0 ? ` (${result.failed} failed)` : "";
                    dispatchToast({
                      type: "success",
                      message: `${result.sent} ${channel} sent${blockedNote}${failedNote}.`,
                    });
                  } catch (e: any) {
                    setShowConfirm(false);
                    dispatchToast({
                      type: "error",
                      message:
                        e?.response?.data?.error ||
                        `Send failed: ${e?.message || "unknown error"}`,
                    });
                  } finally {
                    setSending(false);
                  }
                }}
              >
                {sending ? "Sending..." : "Confirm Send"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBulkSend;
